import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import api from "../services/api";

interface InactivityHandlerProps {
  onLogout?: () => void;
}

interface SessionConfig {
  timeout_seconds: number;
  warning_seconds: number;
}

const InactivityHandler: React.FC<InactivityHandlerProps> = ({
  onLogout,
}) => {
  const auth = useAuth();
  const navigate = useNavigate();

  // Check login state from AuthContext or session storage
  const isLoggedIn = Boolean(auth?.isLoggedIn || sessionStorage.getItem("authToken"));

  const getSessionConfig = useCallback((): { timeout: number; promptDuration: number } | null => {
    try {
      const storedConfig = sessionStorage.getItem("sessionConfig");
      if (storedConfig) {
        const config: SessionConfig = JSON.parse(storedConfig);
        if (config.timeout_seconds > 0 && config.warning_seconds > 0) {
          return {
            timeout: config.timeout_seconds * 1000,
            promptDuration: config.warning_seconds * 1000,
          };
        }
      }
    } catch (error) {
      console.warn("Failed to parse sessionConfig", error);
    }
    return null;
  }, []);

  const [timing, setTiming] = useState(getSessionConfig);
  const [showModal, setShowModal] = useState(false);
  const [remaining, setRemaining] = useState<number>(
    () => Math.floor((timing?.promptDuration || 0) / 1000),
  );
  const countdownRef = useRef<number | null>(null);

  // Sync session config dynamically whenever user login state changes
  useEffect(() => {
    if (isLoggedIn) {
      const updatedTiming = getSessionConfig();
      setTiming(updatedTiming);
      if (updatedTiming) {
        setRemaining(Math.floor(updatedTiming.promptDuration / 1000));
      }
    }
  }, [isLoggedIn, getSessionConfig]);

  // Logout user cleanly and notify Login.tsx
  const handleLogout = useCallback(() => {
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setShowModal(false);

    // Set message for Login.tsx to display session expired modal
    sessionStorage.setItem(
      "sessionExpiredMessage",
      "Your session has expired due to inactivity. Please log in again.",
    );
    sessionStorage.removeItem("sessionConfig");

    if (auth?.logout) {
      auth.logout();
    } else {
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("userDetails");
    }

    if (onLogout) onLogout();
    navigate("/login", { replace: true });
  }, [auth, onLogout, navigate]);

  // Start countdown before auto logout using timestamp diff to avoid background tab throttling
  const startPromptTimer = useCallback(() => {
    if (!timing) return;
    const expireAt = Date.now() + timing.promptDuration;
    setRemaining(Math.floor(timing.promptDuration / 1000));

    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    countdownRef.current = window.setInterval(() => {
      const diffMs = expireAt - Date.now();
      const secsRemaining = Math.max(0, Math.ceil(diffMs / 1000));
      setRemaining(secsRemaining);

      if (diffMs <= 0) {
        if (countdownRef.current) {
          window.clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        handleLogout();
      }
    }, 1000) as unknown as number;
  }, [timing?.promptDuration, handleLogout]);

  // Called when user becomes idle (no activity for timeout ms)
  const onIdle = useCallback(() => {
    if (!isLoggedIn) return;
    setShowModal(true);
    startPromptTimer();
  }, [isLoggedIn, startPromptTimer]);

  // Called when user becomes active again
  const onActive = () => {
    // Explicit action required: user must click "Yes" to confirm continuation
  };

  const { reset } = useIdleTimer({
    timeout: timing?.timeout || 0,
    onIdle,
    onActive,
    debounce: 500,
    crossTab: true, // handles multi-tab sessions across browser tabs
    disabled: !isLoggedIn || !timing, // only active when logged in with session timing
  });

  // Reset timer and refresh backend session when user chooses to continue
  const resetPrompt = async () => {
    const authToken = sessionStorage.getItem("authToken");

    if (!authToken) {
      handleLogout();
      return;
    }

    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    setShowModal(false);
    if (timing) {
      setRemaining(Math.floor(timing.promptDuration / 1000));
    }

    try {
      // Renew token on backend to extend active session
      const response = await api.post("/auth/refresh-token");
      const newToken = response?.data?.token?.remember_token;
      if (newToken) {
        sessionStorage.setItem("authToken", newToken);
        if (auth?.login) {
          auth.login(newToken, auth.userDetails);
        }
      }
      if (response?.data?.sessionConfig) {
        const sessionConfig: SessionConfig = response.data.sessionConfig;
        sessionStorage.setItem(
          "sessionConfig",
          JSON.stringify(sessionConfig),
        );
        if (sessionConfig.timeout_seconds > 0 && sessionConfig.warning_seconds > 0) {
          setTiming({
            timeout: sessionConfig.timeout_seconds * 1000,
            promptDuration: sessionConfig.warning_seconds * 1000,
          });
        }
      }
    } catch (error) {
      console.warn("Session refresh warning, continuing with existing active token:", error);
    }

    reset();
  };

  // cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        window.clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, []);

  // Do not render anything for unauthenticated users
  if (!isLoggedIn) {
    return null;
  }

  return (
    <Modal show={showModal} backdrop="static" centered keyboard={false}>
      <Modal.Header>
        <Modal.Title> Timeout Warning!!</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* <p>You have been inactive for 20 minutes.</p> */}
        <p>
          You will be automatically logged out in <strong>{remaining}</strong>{" "}
          seconds.
        </p>
        <p>Do you want to Continue?</p>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: 'center' }}>
        <Button variant="primary" onClick={resetPrompt}>
          Yes
        </Button>
        <Button variant="danger" onClick={handleLogout}>
          No
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InactivityHandler;